import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import { EmptyUI, ScrollLoadMore, SubPurchaseItem } from 'components';
import { NotifyService } from 'config';
import {
  PopupInformCantPurchase,
  TransactionHistoryItem,
} from 'features/credits/components';
import { MODULE_TYPE } from 'models';
import { AC_BASE_REQUEST_STATUS } from 'models/create-request';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from 'routers/routes';
import { creditsApi } from 'services/credits';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  updateDataTransactionHistoryByCash,
  updateDataTransactionHistoryByCredit,
} from 'store/reducers/credits';
import {
  getCurrentBalanceCreditAction,
  getSubscriptionPurchasedAction,
} from 'store/reducers/credits/actionTypes';
import { setGlobalLoading } from 'store/reducers/global';
import {
  BuyCreditsButton,
  StyledCurrentBalanceTitle,
  StyledCurrentBalanceValue,
  StyledOverviewCredits,
} from './styles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const OverviewCredits = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    currentBalance,
    subsPurchased,
    transactionHistoryByCash,
    transactionHistoryByCredit,
  } = useAppSelector((state) => state.credits);
  const { parent } = useAppSelector((state) => state.auth);
  const { baseRequest } = useAppSelector((state) => state.overview);

  const [tabValue, setValue] = useState<number>(0);
  const [openInformCantPurchase, setOpenInformCantPurchase] =
    useState<boolean>(false);

  const subPurchaseWithPack = subsPurchased?.records?.filter(
    (item) => item.package !== null,
  );

  const isAllowAccessCredit = [
    AC_BASE_REQUEST_STATUS.ACTIVE,
    AC_BASE_REQUEST_STATUS.TRIAL_COMPLETED,
  ].includes(baseRequest?.status);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);

    const parentId = parent?.parent_id || '';
    switch (newValue) {
      case 1:
        // Load credit transactions
        getTransactionHistoryByCredit();
        break;
      case 2:
        // Load cash transactions
        getTransactionHistoryByCash();
        break;
      default:
        // Load subscription purchases
        dispatch(
          getSubscriptionPurchasedAction({
            data: {
              has_balance: false,
              page: 1,
              limit: 20,
              parent_id: parentId,
            },
          }),
        );
        break;
    }
  };

  const loadMoreSubscriptionPurchases = () => {
    const parentId = parent?.parent_id || '';
    dispatch(
      getSubscriptionPurchasedAction({
        data: {
          limit: 20,
          page: subsPurchased.metadata.page + 1,
          has_balance: false,
          parent_id: parentId,
        },
      }),
    );
  };

  const loadMoreTransactionHistory = (type: 'credit' | 'cash') => {
    switch (type) {
      case 'credit':
        getTransactionHistoryByCredit(
          transactionHistoryByCredit.metadata.page + 1,
        );
        break;
      case 'cash':
        getTransactionHistoryByCash(transactionHistoryByCash.metadata.page + 1);
        break;
      default:
        break;
    }
  };

  const handlePurchasePackage = () => {
    if (!isAllowAccessCredit) setOpenInformCantPurchase(true);
    else navigate(APP_ROUTES.CREDITS.BUY_PACKAGE.to);
  };

  const getTransactionHistoryByCredit = async (offset?: number) => {
    dispatch(setGlobalLoading(true));
    await creditsApi
      .getTransactionHistoryByCredit({
        offset: offset || 0,
        limit: 20,
        code: 'SC,JR,CR,DR,PR',
        module_type: MODULE_TYPE.AC,
      })
      .then(({ data }) => {
        dispatch(updateDataTransactionHistoryByCredit(data));
      })
      .catch((e) => {
        console.log('Error get transaction history', e);
        NotifyService.error(e);
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  };

  const getTransactionHistoryByCash = async (offset?: number) => {
    dispatch(setGlobalLoading(true));
    await creditsApi
      .getTransactionHistoryByCash({
        offset: offset || 0,
        limit: 20,
      })
      .then(({ data }) => {
        dispatch(updateDataTransactionHistoryByCash(data));
      })
      .catch((e) => {
        console.log('Error get transaction history', e);
        NotifyService.error(e);
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  };

  useEffect(() => {
    if (!baseRequest) {
      navigate(APP_ROUTES.HOW_IT_WORKS.to);
    } else {
      dispatch(
        getCurrentBalanceCreditAction({
          data: { parentId: parent?.parent_id },
        }),
      );
      dispatch(
        getSubscriptionPurchasedAction({
          data: {
            limit: 20,
            page: 1,
            has_balance: false,
            parent_id: parent?.parent_id,
          },
        }),
      );
    }
    // eslint-disable-next-line
  }, [dispatch, baseRequest]);

  if (!subPurchaseWithPack) {
    return null;
  }

  return (
    <Stack spacing={4} mb={4}>
      {openInformCantPurchase && (
        <PopupInformCantPurchase
          onClose={() => setOpenInformCantPurchase(false)}
        />
      )}
      <Stack spacing={1}>
        <Typography variant="Web_Title_22">Credits</Typography>
        <Typography variant="Web_Label_18">
          Manage your booking credits
        </Typography>
      </Stack>
      <StyledOverviewCredits direction={{ xs: 'column', md: 'row' }}>
        <Stack spacing={0}>
          <Stack direction={{ xs: 'row', md: 'column' }} spacing={1}>
            <StyledCurrentBalanceTitle>
              Current Balance
            </StyledCurrentBalanceTitle>
            <StyledCurrentBalanceValue>
              ~${currentBalance}
            </StyledCurrentBalanceValue>
          </Stack>
          {currentBalance < 100 && (
            <Typography variant="Web_Label_14" color={'primary.main'}>
              Low credit balance, please top up now to avoid disruption of our
              services.
            </Typography>
          )}
        </Stack>
        <BuyCreditsButton variant="contained" onClick={handlePurchasePackage}>
          Buy Credits
        </BuyCreditsButton>
      </StyledOverviewCredits>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleChange}
            aria-label="basic tabs example"
            sx={{
              '& .MuiTabs-flexContainer': {
                overflow: 'auto',
              },
              '& .MuiTabs-flexContainer::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Tab
              sx={{ textTransform: 'unset' }}
              label="Purchase History"
              {...a11yProps(0)}
            />
            <Tab
              sx={{ textTransform: 'unset' }}
              label="TXN By Credit"
              {...a11yProps(1)}
            />
            <Tab
              sx={{ textTransform: 'unset' }}
              label="TXN By Cash"
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={tabValue} index={0}>
          <ScrollLoadMore
            data={subsPurchased.records}
            metadata={subsPurchased.metadata}
            loadMore={loadMoreSubscriptionPurchases}
          >
            <Stack spacing={2}>
              {subPurchaseWithPack?.length > 0 ? (
                subPurchaseWithPack?.map((subscription) => (
                  <SubPurchaseItem key={subscription.id} data={subscription} />
                ))
              ) : (
                <EmptyUI content="You don’t have any transactions yet." />
              )}
            </Stack>
          </ScrollLoadMore>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <ScrollLoadMore
            data={transactionHistoryByCredit.data}
            metadata={transactionHistoryByCredit.metadata}
            loadMore={() => loadMoreTransactionHistory('credit')}
          >
            <Stack spacing={2}>
              {transactionHistoryByCredit.data?.length > 0 ? (
                transactionHistoryByCredit.data?.map((item) => (
                  <Fragment key={item.id}>
                    <TransactionHistoryItem
                      mathSigns={item.TransactionType.sign}
                      txnTitle={item.TransactionType.title}
                      txnNumber={item.doc_ref_no}
                      createdAt={item.createdAt}
                      totalAmount={Number(item.total_amount)}
                      remark={item.remark}
                      status={'success'}
                      paymentMethodType={item.payment_method_type}
                    />
                  </Fragment>
                ))
              ) : (
                <EmptyUI content="You don’t have any transactions yet." />
              )}
            </Stack>
          </ScrollLoadMore>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}>
          <ScrollLoadMore
            data={transactionHistoryByCash.data}
            metadata={transactionHistoryByCash.metadata}
            loadMore={() => loadMoreTransactionHistory('cash')}
          >
            <Stack spacing={2}>
              {transactionHistoryByCash.data?.length > 0 ? (
                transactionHistoryByCash.data?.map((item) => (
                  <Fragment key={item.id}>
                    <TransactionHistoryItem
                      mathSigns={'+'}
                      txnTitle={item.transaction_type?.title}
                      txnNumber={item.number}
                      createdAt={item.created_at}
                      totalAmount={item.total_amount}
                      remark={item.remark}
                      status={item.status}
                      paymentMethodType={item.payment_method_type}
                    />
                  </Fragment>
                ))
              ) : (
                <EmptyUI content="You don’t have any transactions yet." />
              )}
            </Stack>
          </ScrollLoadMore>
        </CustomTabPanel>
      </Box>
    </Stack>
  );
};

export default OverviewCredits;
